import type { AdvisorDraftLeg, AdvisorRegion } from './types';
import { normalizeKeywordsToHashtags } from '@itineraries/features/itinerary-builder/model/hashtag-options';
import { hotelPreferencesFromNotes } from '@travel-planner/recommendation';
import { normalizeMood } from './cms-mood';
import { displayRegionTitle } from './region-layout';

// Landing moods → the catalog hashtag vocabulary the sold-history
// recommender already knows (same map as eval's free-text interests).
const LANDING_MOOD_TO_INTEREST: Record<string, string> = {
  adventure: 'adventure',
  coast: 'beach',
  culture: 'culture',
  gastronomy: 'food',
  heritage: 'history',
  nature: 'nature',
  romance: 'anniversary',
  sailing: 'yacht',
  urban: 'city',
  wellness: 'spa',
  wildlife: 'nature',
  wine: 'wine',
};

const CITY_ALIASES: Record<string, string> = {
  lisboa: 'Lisbon',
  lisbon: 'Lisbon',
  lissabon: 'Lisbon',
  лиссабон: 'Lisbon',
  лисбон: 'Lisbon',
  oporto: 'Porto',
  porto: 'Porto',
  порту: 'Porto',
  sintra: 'Sintra',
  синтра: 'Sintra',
  cascais: 'Cascais',
  кашкайш: 'Cascais',
  belem: 'Belém',
  belém: 'Belém',
  estoril: 'Estoril',
  queluz: 'Queluz',
  algarve: 'Albufeira',
  albufeira: 'Albufeira',
  faro: 'Faro',
  lagos: 'Lagos',
  madeira: 'Funchal',
  funchal: 'Funchal',
  douro: 'Pinhão',
  pinhão: 'Pinhão',
  pinhao: 'Pinhão',
  azores: 'Ponta Delgada',
  açores: 'Ponta Delgada',
  coimbra: 'Coimbra',
  evora: 'Évora',
  évora: 'Évora',
  alentejo: 'Évora',
  madrid: 'Madrid',
  barcelona: 'Barcelona',
  seville: 'Seville',
  sevilla: 'Seville',
};

const SATELLITE_CITIES = new Set([
  'sintra',
  'cascais',
  'belém',
  'belem',
  'estoril',
  'queluz',
]);

const BRIEF_CITY_NEEDLES = Object.keys(CITY_ALIASES).sort(
  (a, b) => b.length - a.length
);

const matchBriefCities = (
  text: string,
  allow: (city: string) => boolean
): string[] => {
  const lower = text.toLowerCase();
  if (!lower.trim()) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const needle of BRIEF_CITY_NEEDLES) {
    const re = new RegExp(`(?:^|[^\\p{L}])${needle}(?:$|[^\\p{L}])`, 'iu');
    if (!re.test(lower)) continue;
    const city = CITY_ALIASES[needle];
    if (!city || !allow(city)) continue;
    const key = city.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(city);
  }
  return out;
};

export const citiesForGenerate = (
  regionTitles: readonly string[]
): string[] => {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const title of regionTitles) {
    // Compound XT titles ("Madrid & Surroundings") must resolve to the hub
    // city the builder overnight plan understands — never the whole string.
    const fromName = matchBriefCities(title, () => true);
    const short = displayRegionTitle(title);
    const mapped = fromName[0] ?? CITY_ALIASES[short.toLowerCase()];
    // Unknown labels (test stubs, free text) are not stay cities.
    if (!mapped) continue;
    const key = mapped.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(mapped);
  }
  return out;
};

/**
 * Destination is overnight hubs. Notes may add same-metro satellites only.
 * A destination naming no city (a country) gives none: the recommender then
 * picks its best-selling cities.
 */
export const citiesFromRequestBrief = (
  destination: string | null | undefined,
  notes: string | null | undefined
): string[] => {
  const dest = destination?.trim() ?? '';
  const extra = notes?.trim() ?? '';
  const destCities = matchBriefCities(dest, () => true);
  const noteCities = matchBriefCities(extra, (city) => {
    if (destCities.length === 0) return true;
    return SATELLITE_CITIES.has(city.toLowerCase());
  });
  const out: string[] = [];
  const seen = new Set<string>();
  for (const city of [...destCities, ...noteCities]) {
    const key = city.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(city);
  }
  return out;
};

/**
 * The city a region's Generate is built around: the first city the region
 * lists in XT (its hub — Porto, Lisbon, Madrid). The recommender makes every
 * city it is given an overnight stay, so a region sends one city, never its
 * whole list (Toledo/Segovia stay day-trips off the Madrid hub).
 */
export const regionHubCity = (region: AdvisorRegion): string | null => {
  const raw = region.cities?.find((city) => city.trim())?.trim() ?? null;
  if (raw) {
    return CITY_ALIASES[raw.toLowerCase()] ?? displayRegionTitle(raw);
  }
  return citiesForGenerate([region.title])[0] ?? null;
};

/** Hub cities of the regions, in order; a region listing none is skipped. */
export const regionHubCities = (
  regionIds: readonly string[],
  regions: readonly AdvisorRegion[]
): string[] =>
  regionIds.flatMap((id) => {
    const region = regions.find((entry) => entry.id === id);
    const hub = region ? regionHubCity(region) : null;
    return hub ? [hub] : [];
  });

/**
 * A chapter's day is built around where it stays: its base hotel's city,
 * else its region's hub city, else a city read from its title.
 */
export const chapterGenerateCity = (
  leg: Pick<AdvisorDraftLeg, 'regionId' | 'regionTitle' | 'baseHotel'>,
  regions: readonly AdvisorRegion[]
): string | null => {
  const hotelCity = leg.baseHotel?.subtitle?.trim();
  if (hotelCity) return hotelCity;
  const region = regions.find((entry) => entry.id === leg.regionId);
  const hub = region ? regionHubCity(region) : null;
  return hub ?? citiesForGenerate([leg.regionTitle])[0] ?? null;
};

/** Schema default for an empty rail — enough nights that two cities don't 1+1. */
export const EMPTY_GENERATE_DAYS = 7;

export type GeneratePreviewTarget = {
  cities: string[];
  durationDays: number;
  legId: string | null;
  dayId: string | null;
  /** The day's picks become suggestions instead of filling it. */
  suggest?: boolean;
  regionIds?: readonly string[];
  categoryHashtags?: readonly string[];
  briefMoods?: readonly string[];
  briefPace?: 'relaxed' | 'balanced' | 'active' | null;
  childrenInParty?: boolean;
};

type GenerateLegInput = {
  id: string;
  regionTitle: string;
  days: readonly { id?: string }[];
};

/** Existing days stay that long. Empty rail uses a full-trip default. */
export const durationForGenerate = (
  dayCount: number,
  emptyDays = EMPTY_GENERATE_DAYS
): number => (dayCount > 0 ? dayCount : Math.max(1, emptyDays));

/** Nights between YYYY-MM-DD stay dates. Sep 10–15 → 5. */
export const durationFromStayDates = (
  startOn: string | null | undefined,
  finishOn: string | null | undefined
): number | null => {
  if (!startOn || !finishOn) return null;
  const start = Date.parse(`${startOn.slice(0, 10)}T00:00:00.000Z`);
  const finish = Date.parse(`${finishOn.slice(0, 10)}T00:00:00.000Z`);
  if (!Number.isFinite(start) || !Number.isFinite(finish) || finish <= start) {
    return null;
  }
  const nights = Math.round((finish - start) / 86_400_000);
  return Math.min(30, Math.max(1, nights));
};

/** Sold-mass month from the request start date. Oct 12 → 10. */
export const monthFromStayDates = (
  startOn: string | null | undefined
): number | null => {
  if (!startOn) return null;
  const start = Date.parse(`${startOn.slice(0, 10)}T00:00:00.000Z`);
  if (!Number.isFinite(start)) return null;
  return new Date(start).getUTCMonth() + 1;
};

export const countryFromRequestBrief = (
  destination: string | null | undefined
): string | null => {
  const text = destination?.trim().toLowerCase() ?? '';
  if (!text) return null;
  if (
    /(?:^|[^\p{L}])(spain|madrid|barcelona|seville|sevilla|mallorca)(?:$|[^\p{L}])/iu.test(
      text
    )
  ) {
    return 'Spain';
  }
  if (
    /(?:^|[^\p{L}])(portugal|lisbon|lisboa|porto|oporto|algarve|madeira|azores|douro|alentejo|coimbra)(?:$|[^\p{L}])/iu.test(
      text
    )
  ) {
    return 'Portugal';
  }
  return null;
};

/** Brief cities only apply when the selected country still matches the request. */
export const seedCitiesForCountry = (
  country: string,
  seedDestination: string | null | undefined,
  seedCities: readonly string[]
): readonly string[] => {
  const selected = country.trim().toLowerCase();
  if (!selected) return seedCities;
  const brief = countryFromRequestBrief(seedDestination);
  if (brief) {
    return brief.toLowerCase() === selected ? seedCities : [];
  }
  const dest = seedDestination?.trim().toLowerCase() ?? '';
  if (!dest) return seedCities;
  if (dest === selected || dest.includes(selected) || selected.includes(dest)) {
    return seedCities;
  }
  return [];
};

/** Day 1 = arrival programme. Later days skip the builder's checkout night. */
export const durationForDayGenerate = (dayIndex: number): number =>
  dayIndex <= 0 ? 1 : dayIndex + 2;

/**
 * Stay cities for Generate, in priority order:
 * 1. Chapters already on the rail (draft region titles)
 * 2. Explicitly selected region chips / map focus hubs (`listedTitles`)
 * 3. Request brief cities (`seedTitles`) when no chip is selected
 *
 * A selected Madrid chip must never lose to a country-only brief — that
 * free-roam is what dropped Barcelona/Seville onto a Madrid request.
 */
export const titlesForGenerate = (
  listedTitles: readonly string[],
  draftTitles: readonly string[],
  seedTitles: readonly string[] = []
): readonly string[] =>
  draftTitles.length > 0
    ? draftTitles
    : listedTitles.length > 0
      ? listedTitles
      : seedTitles;

export const buildGenerateTarget = (input: {
  listedTitles: readonly string[];
  legs: readonly GenerateLegInput[];
  onlyLegId: string | null;
  onlyDayId?: string | null;
  emptyDurationDays?: number;
  seedTitles?: readonly string[];
  /** The chapter's city for a day's Generate (`chapterGenerateCity`). */
  cities?: readonly string[];
}): GeneratePreviewTarget => {
  if (input.onlyDayId && input.onlyLegId) {
    const leg = input.legs.find((entry) => entry.id === input.onlyLegId);
    const dayIndex =
      leg?.days.findIndex((day) => day.id === input.onlyDayId) ?? -1;
    return {
      cities: input.cities?.length
        ? citiesForGenerate(input.cities)
        : citiesForGenerate(leg ? [leg.regionTitle] : []),
      durationDays: durationForDayGenerate(dayIndex),
      legId: input.onlyLegId,
      dayId: input.onlyDayId,
    };
  }
  if (input.onlyLegId) {
    const leg = input.legs.find((entry) => entry.id === input.onlyLegId);
    return {
      cities: citiesForGenerate(leg ? [leg.regionTitle] : []),
      durationDays: durationForGenerate(leg?.days.length ?? 0),
      legId: input.onlyLegId,
      dayId: null,
    };
  }
  const dayCount = input.legs.reduce((sum, leg) => sum + leg.days.length, 0);
  const titles = titlesForGenerate(
    input.listedTitles,
    dayCount > 0 ? input.legs.map((leg) => leg.regionTitle) : [],
    input.seedTitles ?? []
  );
  return {
    cities: citiesForGenerate(titles),
    durationDays: durationForGenerate(dayCount, input.emptyDurationDays),
    legId: null,
    dayId: null,
  };
};

export const keywordsFromRequestNotes = (
  notes: string | null | undefined
): string[] => {
  const text = notes?.toLowerCase() ?? '';
  if (!text.trim()) return [];
  const pairs: Array<[string, string]> = [
    ...Object.entries(LANDING_MOOD_TO_INTEREST),
    ['beach', 'beach'],
    ['food', 'food'],
  ];
  const hits: string[] = [];
  const seen = new Set<string>();
  for (const [needle, interest] of pairs) {
    const re = new RegExp(`(?:^|[^\\p{L}])${needle}(?:$|[^\\p{L}])`, 'iu');
    if (!re.test(text) || seen.has(interest)) continue;
    seen.add(interest);
    hits.push(interest);
  }
  return normalizeKeywordsToHashtags(hits);
};

/** The request notes' hotel preferences, for trip Generate (XT-806). */
export const hotelPreferencesFromRequestNotes = (
  notes: string | null | undefined
): string[] => hotelPreferencesFromNotes(notes);

export const keywordsForGenerate = (
  moods: readonly string[],
  notes?: string | null
): string[] => {
  const mapped = moods.map((mood) => {
    const key = normalizeMood(mood);
    return LANDING_MOOD_TO_INTEREST[key] ?? mood;
  });
  return normalizeKeywordsToHashtags([
    ...mapped,
    ...keywordsFromRequestNotes(notes),
  ]);
};
